import tokenClient from "api/account/tokenClient";
import axios from "axios";

const accountApiBaseUrl = process.env.REACT_APP_ACCOUNT_API_BASE_URL;

interface QueueDetails {
  queueName: string;
  queueUrl: string;
  queueTitle: string;
  filters: string[];
}

interface IrisResponseCommonDetails {
  applicationId: string;
  serviceBusNamespace: string;
  queues: QueueDetails[];
  maxQueueCount: number;
}

export interface ClientSecret {
  secretId: string;
  description: string;
  secretHint: string;
  generatedTime: string;
  expiryTime: string;
}

export interface ClientSecretWithValue extends ClientSecret {
  secretValue: string;
}

export interface IrisPostResponse extends IrisResponseCommonDetails {
  applicationSecrets: [ClientSecretWithValue];
}

export interface IrisGetResponse extends IrisResponseCommonDetails {
  applicationSecrets: ClientSecret[];
}

export type IrisSummary = IrisGetResponse | IrisPostResponse;

const createHeaders = (accessToken: string): Record<string, string> => ({
  Authorization: `Bearer ${accessToken}`,
});

const httpGet = async <T>(path: string): Promise<T> => {
  const token = await tokenClient.getAccessToken();
  const url = `${accountApiBaseUrl}${path}`;
  const config = { headers: createHeaders(token) };

  const { data } = await axios.get<T>(url, config);

  return data;
};

const httpPost = async <T>(
  path: string,
  body = {},
  extraHeaders?: Record<string, string>
): Promise<T> => {
  const token = await tokenClient.getAccessToken();
  const url = `${accountApiBaseUrl}${path}`;
  const config = { headers: { ...createHeaders(token), ...extraHeaders } };

  const { data } = await axios.post<T>(url, body, config);

  return data;
};

const httpDelete = async <T>(path: string): Promise<T> => {
  const token = await tokenClient.getAccessToken();
  const url = `${accountApiBaseUrl}${path}`;
  const config = { headers: createHeaders(token) };

  const { data } = await axios.delete(url, config);

  return data;
};

const httpPut = async <T>(path: string, body = {}): Promise<T> => {
  const token = await tokenClient.getAccessToken();
  const url = `${accountApiBaseUrl}${path}`;
  const config = { headers: createHeaders(token) };

  const { data } = await axios.put<T>(url, body, config);

  return data;
};

export const getIrisConnectionDetails = async (): Promise<IrisGetResponse> =>
  httpGet<IrisGetResponse>("/iris");

export const createIrisConnection = async (): Promise<IrisPostResponse> =>
  httpPost<IrisPostResponse>("/iris");

export const createIrisSecret = async (): Promise<ClientSecretWithValue> =>
  httpPost<ClientSecretWithValue>("/iris/secret");

export const deleteIrisSecret = async (secretId: string): Promise<void> =>
  httpDelete(`/iris/secret/${secretId}`);

export const deleteIrisQueue = async (queueTitle: string): Promise<void> =>
  httpDelete(`/iris/queue?queueTitle=${queueTitle}`);

export const createIrisQueue = async (queueTitle: string): Promise<void> => {
  const body = { queueTitle: queueTitle };
  return httpPost<void>("/iris/queue", body);
};

export const updateQueueFilters = async (
  queueTitle: string,
  filters: string[]
): Promise<void> => httpPut(`/iris/queue/${queueTitle}/filter`, filters);

export const submitRemitNotification = async (file: File): Promise<void> => {
  const xmlContent = await file.text();
  return httpPost<void>("/remit/submit", xmlContent, {
    "Content-Type": "application/xml",
  });
};
